/**
 * A project's phases and their milestones, on a calendar -- the first thing under the project's tab strip.
 *
 * ED, 2026-11-04: "A Project will have Phases for children and each phase will have a type. Examples include "Sales"
 * and "Execution"", "We will need the ability to track Phases for a project and phases should have Start Date and Due
 * Date. Users should be given the ability to create milestones for a phase", and "Notice how enlil-t9 displays a Project
 * detail. Let's show that in the area below the top tab set."
 *
 * ENLIL-T9'S PLANNING VIEW, KEPT IN ITS SHAPE: a row per phase (expand, name, type, dates, actions) with its milestones
 * indented under it, and a calendar to the right that can be read by Week, Month, Quarter or Year and stepped back and
 * forward. Two things are drawn that Enlil did not: a phase's start-to-due span as a bar (Enlil marked the due date
 * only, because its phases had no reliable start), and a line for today.
 *
 * THE SERVER IS cres_t1/service/project_phase.py. Every write answers with the phases as they now stand, so the view is
 * redrawn from the answer rather than patched locally. A closed project, or a reader without project:create, sees the
 * plan and none of the controls -- the service refuses those writes anyway.
 */
import React, {useCallback, useEffect, useRef, useState} from "react";
import {Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu,
        MenuItem, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography} from "@mui/material";
import {AddOutlined, ChevronLeftOutlined, ChevronRightOutlined, ExpandMoreOutlined, MoreVertOutlined} from "@mui/icons-material";

import {Blue3, Grey2, Grey5, Grey6, Grey7, Grey17, White} from "../../theme/styles";
import {hasPrivilege} from "../../util/SessionContext";

export type Milestone = {USID: string; Name: string; Description?: string | null; StartDate?: string | null;
                         DueDate?: string | null; Status: string; CompletedDate?: string | null; Placement: number};
export type Phase = {USID: string; PhaseType: string; Name: string; StartDate?: string | null; DueDate?: string | null;
                     Placement: number; Counts?: Record<string, number>; ListOfMilestone?: {Milestone?: Milestone[]}};

const PHASE_TYPES = ["Sales", "Execution"];
const MILESTONE_STATUSES = ["NOT STARTED", "IN PROGRESS", "DONE"];
const TYPE_COLOUR: Record<string, string> = {Sales: Blue3, Execution: "#6A4FB3"};
const STATUS_COLOUR: Record<string, string> = {"DONE": "#00A044", "IN PROGRESS": "#B26000", "NOT STARTED": "#7B8291"};
const ROW = 44;
const LEFT = 640;

type Perspective = "Week" | "Month" | "Quarter" | "Year";

/** A 'YYYY-MM-DD' as a local date at midnight, so a day is a day whatever the time zone. */
function day(value?: string | null): Date | null {
    const found = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ""));
    return found ? new Date(Number(found[1]), Number(found[2]) - 1, Number(found[3])) : null;
}
const iso = (when: Date) => `${when.getFullYear()}-${String(when.getMonth() + 1).padStart(2, "0")}-${String(when.getDate()).padStart(2, "0")}`;
const shown = (value?: string | null) => {
    const when = day(value);
    return when ? when.toLocaleDateString(undefined, {month: "short", day: "numeric", year: "numeric"}) : "—";
};
const addDays = (when: Date, n: number) => new Date(when.getFullYear(), when.getMonth(), when.getDate() + n);
const daysBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 86400000);
const milestonesOf = (phase: Phase) => phase.ListOfMilestone?.Milestone ?? [];

/** The calendar's window and its columns, as Enlil drew them: a week by day, a month by day, a year by quarter or month. */
function windowFor(perspective: Perspective, anchor: Date): {from: Date; to: Date; title: string; columns: {label: string; days: number}[]} {
    if (perspective === "Week") {
        const from = addDays(anchor, -((anchor.getDay() + 6) % 7));
        const columns = Array.from({length: 7}, (_, i) => {
            const d = addDays(from, i);
            return {label: `${d.toLocaleDateString(undefined, {weekday: "short"})} ${d.getDate()}`, days: 1};
        });
        const to = addDays(from, 7);
        return {from, to, columns, title: `${shown(iso(from))} – ${shown(iso(addDays(to, -1)))}`};
    }
    if (perspective === "Month") {
        const from = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
        const to = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
        const columns = Array.from({length: daysBetween(from, to)}, (_, i) => ({label: String(i + 1), days: 1}));
        return {from, to, columns, title: from.toLocaleDateString(undefined, {month: "long", year: "numeric"})};
    }
    const from = new Date(anchor.getFullYear(), 0, 1);
    const to = new Date(anchor.getFullYear() + 1, 0, 1);
    const parts = perspective === "Quarter" ? 4 : 12;
    const columns = Array.from({length: parts}, (_, i) => {
        const start = new Date(from.getFullYear(), i * (12 / parts), 1);
        const end = new Date(from.getFullYear(), (i + 1) * (12 / parts), 1);
        return {label: perspective === "Quarter" ? `Q${i + 1}` : start.toLocaleDateString(undefined, {month: "short"}),
                days: daysBetween(start, end)};
    });
    return {from, to, columns, title: String(from.getFullYear())};
}

function step(perspective: Perspective, anchor: Date, direction: number): Date {
    if (perspective === "Week") return addDays(anchor, 7 * direction);
    if (perspective === "Month") return new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1);
    return new Date(anchor.getFullYear() + direction, 0, 1);
}

export default function ProjectPhases({api, projectObjectUSID, status, refresh}: {
    api: any;
    /** The project's LOGICAL object, which phases key on. */
    projectObjectUSID: string;
    /** OPEN takes changes; CLOSED shows the plan as it was left. */
    status?: string | null;
    /** Changes when something is filed or removed below, so each phase's "filed" count is read again. */
    refresh?: string;
}) {
    const [phases, setPhases] = useState<Phase[] | null>(null);
    const [problem, setProblem] = useState<string | null>(null);
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
    const [perspective, setPerspective] = useState<Perspective>("Quarter");
    const [anchor, setAnchor] = useState<Date>(() => new Date());
    const [phaseDialog, setPhaseDialog] = useState<{phase?: Phase} | null>(null);
    const [milestoneDialog, setMilestoneDialog] = useState<{phase: Phase; milestone?: Milestone} | null>(null);
    const [removing, setRemoving] = useState<{phase?: Phase; milestone?: Milestone} | null>(null);
    const [menu, setMenu] = useState<{anchor: HTMLElement; phase: Phase; milestone?: Milestone} | null>(null);

    const writable = String(status ?? "").toUpperCase() === "OPEN" && hasPrivilege("project:create");
    const take = useCallback((answer: any) => {
        setPhases(answer?.data?.PlatformMessage?.ListOfProjectPhase?.ProjectPhase ?? []);
        setProblem(null);
    }, []);
    const refusal = (fallback: string) => (problem: any) => { throw new Error(problem?.response?.data?.Cause ?? fallback); };

    useEffect(() => {
        let live = true;
        api.get("t1/v1/project_phases", {params: {USID: projectObjectUSID}})
            .then((answer: any) => { if (live) take(answer); })
            .catch((failed: any) => { if (live) { setPhases([]); setProblem(failed?.response?.data?.Cause ?? "The phases could not be read."); } });
        return () => { live = false; };
    }, [api, projectObjectUSID, take, refresh]);

    // THE CALENDAR OPENS ON THE WORK, not on an empty stretch: on the earliest phase start when the plan has one. Once,
    // when the plan first arrives -- a save redraws the plan and must not move the calendar the reader has stepped to.
    const anchored = useRef(false);
    useEffect(() => {
        if (anchored.current || phases === null) return;
        anchored.current = true;
        const first = phases.map(p => day(p.StartDate)).filter((d): d is Date => Boolean(d)).sort((a, b) => a.getTime() - b.getTime())[0];
        if (first) setAnchor(first);
    }, [phases]);

    const view = windowFor(perspective, anchor);
    const span = daysBetween(view.from, view.to);
    const at = (when: Date) => (daysBetween(view.from, when) / span) * 100;
    const today = new Date(); today.setHours(0, 0, 0, 0);

    const send = (call: Promise<any>, fallback: string) => call.then(take).catch(refusal(fallback));
    const moveSwap = (list: {USID: string; Placement: number}[], index: number, by: number, path: string) => {
        const other = list[index + by];
        if (!other) return;
        send(api.put(path, {USID: list[index].USID, Placement: other.Placement}), "That could not be moved.")
            .catch((failed: Error) => setProblem(failed.message));
    };

    const bar = (start: Date | null, due: Date | null, colour: string, thin: boolean) => {
        if (!start || !due) return null;
        const left = Math.max(0, at(start)), right = Math.min(100, at(addDays(due, 1)));
        if (right <= 0 || left >= 100) return null;
        return <Box sx={{position: "absolute", left: `${left}%`, width: `${Math.max(0.6, right - left)}%`, top: thin ? 19 : 14,
                         height: thin ? 6 : 16, borderRadius: 1, backgroundColor: colour, opacity: thin ? 0.35 : 0.85}}/>;
    };
    const mark = (when: Date | null, colour: string, square: boolean, hollow: boolean, label: string) => {
        if (!when) return null;
        const left = at(addDays(when, 0)) + (50 / span);
        if (left < 0 || left > 100) return null;
        return (
            <Tooltip title={label}>
                <Box sx={{position: "absolute", left: `calc(${left}% - 7px)`, top: 15, width: 14, height: 14,
                          borderRadius: square ? "3px" : "50%", border: `2px solid ${colour}`, boxSizing: "border-box",
                          backgroundColor: hollow ? White : colour}}/>
            </Tooltip>
        );
    };
    const gridLines = (
        <>
            {view.columns.reduce<{left: number; nodes: React.ReactNode[]}>((acc, column, i) => {
                acc.nodes.push(<Box key={i} sx={{position: "absolute", top: 0, bottom: 0, left: `${(acc.left / span) * 100}%`,
                                                 borderLeft: i ? `1px solid ${Grey2}` : "none"}}/>);
                acc.left += column.days;
                return acc;
            }, {left: 0, nodes: []}).nodes}
            {today >= view.from && today < view.to && (
                <Box sx={{position: "absolute", top: 0, bottom: 0, left: `${at(today) + 50 / span}%`, borderLeft: "2px solid #D93025", opacity: 0.6}}/>
            )}
        </>
    );

    if (phases === null) {
        return <Box sx={{display: "flex", alignItems: "center", gap: 1.25, py: 2}}><CircularProgress size={16}/>
            <Typography sx={{fontSize: 13, color: Grey7}}>Reading the phases…</Typography></Box>;
    }

    const leftCell = (sx: object, children: React.ReactNode) => (
        <Box sx={{display: "flex", alignItems: "center", minWidth: 0, ...sx}}>{children}</Box>
    );
    const header = (
        <Box sx={{display: "flex", height: 36, backgroundColor: "#EDEFF3", borderBottom: `1px solid ${Grey2}`}}>
            <Box sx={{width: LEFT, flexShrink: 0, display: "flex", alignItems: "center", borderRight: `1px solid ${Grey2}`,
                      fontSize: 12, fontWeight: 600, color: Grey7, textTransform: "uppercase", letterSpacing: "0.04em"}}>
                {leftCell({width: 300, pl: "36px"}, "Phase / milestone")}
                {leftCell({width: 104}, "Type / status")}
                {leftCell({width: 100}, "Start")}
                {leftCell({width: 100}, "Due")}
            </Box>
            <Box sx={{flex: 1, display: "flex", minWidth: 0}}>
                {view.columns.map((column, i) => (
                    <Box key={i} sx={{flexGrow: column.days, flexBasis: 0, minWidth: 0, display: "flex", alignItems: "center",
                                      justifyContent: "center", borderLeft: i ? `1px solid ${Grey2}` : "none",
                                      fontSize: 11.5, color: Grey7, overflow: "hidden", whiteSpace: "nowrap"}}>
                        {column.label}
                    </Box>
                ))}
            </Box>
        </Box>
    );

    const phaseRow = (phase: Phase, index: number) => {
        const open = !collapsed.has(phase.USID);
        const milestones = milestonesOf(phase);
        const done = milestones.filter(m => m.Status === "DONE").length;
        const filed = Object.values(phase.Counts ?? {}).reduce((a, b) => a + b, 0);
        const due = day(phase.DueDate);
        const late = due && due < today && done < milestones.length;
        const colour = TYPE_COLOUR[phase.PhaseType] ?? Blue3;
        return (
            <React.Fragment key={phase.USID}>
                <Box sx={{display: "flex", height: ROW, backgroundColor: "#F5F7FA", borderBottom: `1px solid ${Grey2}`}}>
                    <Box sx={{width: LEFT, flexShrink: 0, display: "flex", alignItems: "center", borderRight: `1px solid ${Grey2}`}}>
                        {leftCell({width: 300}, <>
                            <IconButton size="small" sx={{mx: "4px"}} aria-label={open ? "Collapse" : "Expand"}
                                        onClick={() => setCollapsed(current => { const next = new Set(current); if (open) next.add(phase.USID); else next.delete(phase.USID); return next; })}>
                                {open ? <ExpandMoreOutlined sx={{fontSize: 18}}/> : <ChevronRightOutlined sx={{fontSize: 18}}/>}
                            </IconButton>
                            <Box sx={{minWidth: 0}}>
                                <Typography noWrap sx={{fontSize: 14, fontWeight: 600, color: Grey17, cursor: writable ? "pointer" : "default"}}
                                            onClick={() => writable && setPhaseDialog({phase})}>{phase.Name}</Typography>
                                <Typography noWrap sx={{fontSize: 11.5, color: Grey6}}>
                                    {milestones.length ? `${done} of ${milestones.length} milestone${milestones.length === 1 ? "" : "s"} done` : "No milestones"}
                                    {filed ? ` · ${filed} filed` : ""}
                                </Typography>
                            </Box>
                        </>)}
                        {leftCell({width: 104}, <Chip size="small" label={phase.PhaseType}
                                                     sx={{height: 20, fontSize: 11.5, color: colour, border: `1px solid ${colour}`, backgroundColor: White}}/>)}
                        {leftCell({width: 100}, <Typography noWrap sx={{fontSize: 12.5, color: Grey17}}>{shown(phase.StartDate)}</Typography>)}
                        {leftCell({width: 100}, <Typography sx={{fontSize: 12.5, color: late ? "#B3261E" : Grey17, fontWeight: late ? 600 : 400}}>{shown(phase.DueDate)}</Typography>)}
                        {writable && leftCell({ml: "auto", pr: "4px"}, (
                            <IconButton size="small" aria-label={`Actions for ${phase.Name}`} onClick={event => setMenu({anchor: event.currentTarget, phase})}>
                                <MoreVertOutlined sx={{fontSize: 18, color: Grey6}}/>
                            </IconButton>
                        ))}
                    </Box>
                    <Box sx={{flex: 1, position: "relative", minWidth: 0, overflow: "hidden"}}>
                        {gridLines}
                        {bar(day(phase.StartDate), due, colour, false)}
                        {/* STARTED, NO DUE DATE SET: drawn faintly from its start to today, so a phase under way is visible. */}
                        {!due && day(phase.StartDate) && day(phase.StartDate)! <= today && (
                            <Box sx={{position: "absolute", top: 14, height: 16, borderRadius: 1, backgroundColor: colour, opacity: 0.3,
                                      left: `${Math.max(0, at(day(phase.StartDate)!))}%`,
                                      width: `${Math.max(0.6, Math.min(100, at(addDays(today, 1))) - Math.max(0, at(day(phase.StartDate)!)))}%`}}/>
                        )}
                        {mark(due, colour, false, done < milestones.length || !milestones.length,
                              `${phase.Name} due ${shown(phase.DueDate)}`)}
                    </Box>
                </Box>
                {open && milestones.map((milestone, i) => (
                    <Box key={milestone.USID} sx={{display: "flex", height: ROW, backgroundColor: White, borderBottom: `1px solid ${Grey2}`}}>
                        <Box sx={{width: LEFT, flexShrink: 0, display: "flex", alignItems: "center", borderRight: `1px solid ${Grey2}`}}>
                            {leftCell({width: 300, pl: "40px"}, <Box sx={{minWidth: 0}}>
                                <Typography noWrap sx={{fontSize: 13.5, color: Grey17, cursor: writable ? "pointer" : "default"}}
                                            onClick={() => writable && setMilestoneDialog({phase, milestone})}>{milestone.Name}</Typography>
                                {milestone.Description ? <Typography noWrap sx={{fontSize: 11.5, color: Grey6}}>{milestone.Description}</Typography> : null}
                            </Box>)}
                            {leftCell({width: 104}, <Chip size="small" label={milestone.Status}
                                                         sx={{height: 20, fontSize: 10.5, color: STATUS_COLOUR[milestone.Status],
                                                              border: `1px solid ${STATUS_COLOUR[milestone.Status]}`, backgroundColor: White}}/>)}
                            {leftCell({width: 100}, <Typography noWrap sx={{fontSize: 12.5, color: Grey17}}>{shown(milestone.StartDate)}</Typography>)}
                            {leftCell({width: 100}, <Typography noWrap sx={{fontSize: 12.5, color: Grey17}}>{shown(milestone.DueDate)}</Typography>)}
                            {writable && leftCell({ml: "auto", pr: "4px"}, (
                                <IconButton size="small" aria-label={`Actions for ${milestone.Name}`}
                                            onClick={event => setMenu({anchor: event.currentTarget, phase, milestone})}>
                                    <MoreVertOutlined sx={{fontSize: 18, color: Grey6}}/>
                                </IconButton>
                            ))}
                        </Box>
                        <Box sx={{flex: 1, position: "relative", minWidth: 0, overflow: "hidden"}}>
                            {gridLines}
                            {bar(day(milestone.StartDate), day(milestone.DueDate), STATUS_COLOUR[milestone.Status], true)}
                            {mark(day(milestone.DueDate), STATUS_COLOUR[milestone.Status], true, milestone.Status === "NOT STARTED",
                                  `${milestone.Name}: ${milestone.Status.toLowerCase()}, due ${shown(milestone.DueDate)}`)}
                        </Box>
                    </Box>
                ))}
                {open && writable && (
                    <Box sx={{display: "flex", height: 34, borderBottom: `1px solid ${Grey2}`}}>
                        <Box sx={{width: LEFT, flexShrink: 0, pl: "36px", display: "flex", alignItems: "center", borderRight: `1px solid ${Grey2}`}}>
                            <Button size="small" startIcon={<AddOutlined sx={{fontSize: 16}}/>} sx={{textTransform: "none", fontSize: 12.5}}
                                    onClick={() => setMilestoneDialog({phase})}>Add milestone</Button>
                        </Box>
                        <Box sx={{flex: 1, position: "relative"}}>{gridLines}</Box>
                    </Box>
                )}
            </React.Fragment>
        );
    };

    const menuPhase = menu?.phase;
    const siblings = menu ? (menu.milestone ? milestonesOf(menu.phase) : phases) : [];
    const menuIndex = menu ? siblings.findIndex(one => one.USID === (menu.milestone ?? menu.phase).USID) : -1;
    const menuPath = menu?.milestone ? "t1/v1/project_milestones" : "t1/v1/project_phases";

    return (
        <Box>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1.25, flexWrap: "wrap"}}>
                <Typography sx={{fontSize: 16, fontWeight: 600, color: Grey17, mr: 1}}>Phases</Typography>
                {writable && (
                    <Button size="small" variant="contained" startIcon={<AddOutlined/>} sx={{textTransform: "none"}}
                            onClick={() => setPhaseDialog({})}>Add phase</Button>
                )}
                <Box sx={{flex: 1}}/>
                <IconButton size="small" aria-label="Earlier" onClick={() => setAnchor(step(perspective, anchor, -1))}><ChevronLeftOutlined/></IconButton>
                <Typography sx={{fontSize: 13.5, color: Grey17, minWidth: 150, textAlign: "center"}}>{view.title}</Typography>
                <IconButton size="small" aria-label="Later" onClick={() => setAnchor(step(perspective, anchor, 1))}><ChevronRightOutlined/></IconButton>
                <Button size="small" sx={{textTransform: "none"}} onClick={() => setAnchor(new Date())}>Today</Button>
                <ToggleButtonGroup size="small" exclusive value={perspective} onChange={(_, next) => next && setPerspective(next)}>
                    {(["Week", "Month", "Quarter", "Year"] as Perspective[]).map(one => (
                        <ToggleButton key={one} value={one} sx={{textTransform: "none", py: "2px", px: 1.25, fontSize: 12.5}}>{one}</ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>
            {problem && <Typography sx={{fontSize: 12.5, color: "#B3261E", mb: 1}}>{problem}</Typography>}
            <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", overflowX: "auto"}}>
                <Box sx={{minWidth: LEFT + 420}}>
                    {header}
                    {phases.length ? phases.map(phaseRow) : (
                        <Typography sx={{fontSize: 13, color: Grey5, p: 2}}>
                            This project has no phases yet.{writable ? " Add one to plan its work." : ""}
                        </Typography>
                    )}
                </Box>
            </Box>

            <Menu open={Boolean(menu)} anchorEl={menu?.anchor} onClose={() => setMenu(null)}>
                {menu && !menu.milestone && <MenuItem onClick={() => { setPhaseDialog({phase: menu.phase}); setMenu(null); }}>Edit phase</MenuItem>}
                {menu && !menu.milestone && <MenuItem onClick={() => { setMilestoneDialog({phase: menu.phase}); setMenu(null); }}>Add milestone</MenuItem>}
                {menu?.milestone && <MenuItem onClick={() => { setMilestoneDialog({phase: menu.phase, milestone: menu.milestone}); setMenu(null); }}>Edit milestone</MenuItem>}
                {menu?.milestone && MILESTONE_STATUSES.filter(s => s !== menu.milestone!.Status).map(next => (
                    <MenuItem key={next} onClick={() => {
                        send(api.put("t1/v1/project_milestones", {USID: menu.milestone!.USID, Status: next}), "That could not be changed.")
                            .catch((failed: Error) => setProblem(failed.message));
                        setMenu(null);
                    }}>Mark {next.toLowerCase()}</MenuItem>
                ))}
                <MenuItem disabled={menuIndex <= 0} onClick={() => { moveSwap(siblings, menuIndex, -1, menuPath); setMenu(null); }}>Move up</MenuItem>
                <MenuItem disabled={menuIndex < 0 || menuIndex >= siblings.length - 1} onClick={() => { moveSwap(siblings, menuIndex, 1, menuPath); setMenu(null); }}>Move down</MenuItem>
                {menu && (
                    <MenuItem sx={{color: "#B3261E"}} onClick={() => { setRemoving(menu.milestone ? {milestone: menu.milestone} : {phase: menuPhase}); setMenu(null); }}>
                        Remove {menu.milestone ? "milestone" : "phase"}
                    </MenuItem>
                )}
            </Menu>

            {phaseDialog && (
                <PhaseDialog phase={phaseDialog.phase} onCancel={() => setPhaseDialog(null)}
                             onSave={fields => send(phaseDialog.phase
                                 ? api.put("t1/v1/project_phases", {USID: phaseDialog.phase.USID, ...fields})
                                 : api.post("t1/v1/project_phases", {USID: projectObjectUSID, ...fields}), "The phase could not be saved.")
                                 .then(() => setPhaseDialog(null))}/>
            )}
            {milestoneDialog && (
                <MilestoneDialog phase={milestoneDialog.phase} milestone={milestoneDialog.milestone} onCancel={() => setMilestoneDialog(null)}
                                 onSave={fields => send(milestoneDialog.milestone
                                     ? api.put("t1/v1/project_milestones", {USID: milestoneDialog.milestone.USID, ...fields})
                                     : api.post("t1/v1/project_milestones", {PhaseUSID: milestoneDialog.phase.USID, ...fields}), "The milestone could not be saved.")
                                     .then(() => { setMilestoneDialog(null); setCollapsed(current => { const next = new Set(current); next.delete(milestoneDialog.phase.USID); return next; }); })}/>
            )}
            {removing && (
                <RemoveDialog what={removing.milestone ? `the milestone "${removing.milestone.Name}"` : `the phase "${removing.phase!.Name}" and its milestones`}
                              onCancel={() => setRemoving(null)}
                              onConfirm={() => send(removing.milestone
                                  ? api.delete("t1/v1/project_milestones", {params: {USID: removing.milestone.USID}})
                                  : api.delete("t1/v1/project_phases", {params: {USID: removing.phase!.USID}}), "That could not be removed.")
                                  .then(() => setRemoving(null))}/>
            )}
        </Box>
    );
}

/** Save, and keep the dialog open with the server's reason if it refuses. */
function useSave(onSave: (fields: any) => Promise<any>) {
    const [busy, setBusy] = useState(false);
    const [refusal, setRefusal] = useState<string | null>(null);
    const save = (fields: any) => {
        setBusy(true); setRefusal(null);
        onSave(fields).catch((failed: Error) => setRefusal(failed.message)).finally(() => setBusy(false));
    };
    return {busy, refusal, save};
}

const dateField = (label: string, value: string, onChange: (value: string) => void, disabled: boolean) => (
    <TextField label={label} type="date" size="small" value={value} disabled={disabled} sx={{flex: 1}}
               onChange={event => onChange(event.target.value)} slotProps={{inputLabel: {shrink: true}}}/>
);

function PhaseDialog({phase, onCancel, onSave}: {phase?: Phase; onCancel: () => void; onSave: (fields: any) => Promise<any>}) {
    const [type, setType] = useState(phase?.PhaseType ?? "Execution");
    const [name, setName] = useState(phase?.Name ?? "");
    const [start, setStart] = useState(phase?.StartDate ?? "");
    const [due, setDue] = useState(phase?.DueDate ?? "");
    const {busy, refusal, save} = useSave(onSave);
    return (
        <Dialog open onClose={onCancel} fullWidth maxWidth="xs">
            <DialogTitle sx={{fontSize: 16}}>{phase ? "Edit phase" : "Add a phase"}</DialogTitle>
            <DialogContent sx={{display: "flex", flexDirection: "column", gap: 2.5, pt: "8px !important"}}>
                <TextField select size="small" label="Type" value={type} disabled={busy} onChange={event => setType(event.target.value)}>
                    {PHASE_TYPES.map(one => <MenuItem key={one} value={one}>{one}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Name" value={name} autoFocus disabled={busy} onChange={event => setName(event.target.value)}
                           placeholder={type === "Sales" ? "Sales Phase for Northbridge" : "Execution Phase"}/>
                <Box sx={{display: "flex", gap: 1.5}}>
                    {dateField("Start date", start, setStart, busy)}
                    {dateField("Due date", due, setDue, busy)}
                </Box>
                {refusal && <Typography sx={{fontSize: 12.5, color: "#B3261E"}}>{refusal}</Typography>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} sx={{textTransform: "none"}}>Cancel</Button>
                <Button variant="contained" disabled={busy || !name.trim() || Boolean(start && due && due < start)} sx={{textTransform: "none"}}
                        onClick={() => save({PhaseType: type, Name: name.trim(), StartDate: start || null, DueDate: due || null})}>
                    {phase ? "Save" : "Add phase"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function MilestoneDialog({phase, milestone, onCancel, onSave}: {phase: Phase; milestone?: Milestone; onCancel: () => void;
                                                                 onSave: (fields: any) => Promise<any>}) {
    const [name, setName] = useState(milestone?.Name ?? "");
    const [description, setDescription] = useState(milestone?.Description ?? "");
    const [start, setStart] = useState(milestone?.StartDate ?? "");
    const [due, setDue] = useState(milestone?.DueDate ?? "");
    const [status, setStatus] = useState(milestone?.Status ?? "NOT STARTED");
    const {busy, refusal, save} = useSave(onSave);
    return (
        <Dialog open onClose={onCancel} fullWidth maxWidth="xs">
            <DialogTitle sx={{fontSize: 16}}>{milestone ? "Edit milestone" : `Add a milestone to ${phase.Name}`}</DialogTitle>
            <DialogContent sx={{display: "flex", flexDirection: "column", gap: 2.5, pt: "8px !important"}}>
                <TextField size="small" label="Name" value={name} autoFocus disabled={busy} onChange={event => setName(event.target.value)}/>
                <TextField size="small" label="Description" value={description} multiline minRows={2} disabled={busy}
                           onChange={event => setDescription(event.target.value)}/>
                <Box sx={{display: "flex", gap: 1.5}}>
                    {dateField("Start date", start, setStart, busy)}
                    {dateField("Due date", due, setDue, busy)}
                </Box>
                <TextField select size="small" label="Status" value={status} disabled={busy} onChange={event => setStatus(event.target.value)}>
                    {MILESTONE_STATUSES.map(one => <MenuItem key={one} value={one}>{one.charAt(0) + one.slice(1).toLowerCase()}</MenuItem>)}
                </TextField>
                {refusal && <Typography sx={{fontSize: 12.5, color: "#B3261E"}}>{refusal}</Typography>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} sx={{textTransform: "none"}}>Cancel</Button>
                <Button variant="contained" disabled={busy || !name.trim() || Boolean(start && due && due < start)} sx={{textTransform: "none"}}
                        onClick={() => save({Name: name.trim(), Description: description.trim(), StartDate: start || null,
                                             DueDate: due || null, Status: status})}>
                    {milestone ? "Save" : "Add milestone"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function RemoveDialog({what, onCancel, onConfirm}: {what: string; onCancel: () => void; onConfirm: () => Promise<any>}) {
    const {busy, refusal, save} = useSave(() => onConfirm());
    return (
        <Dialog open onClose={onCancel} fullWidth maxWidth="xs">
            <DialogTitle sx={{fontSize: 16}}>Remove {what}?</DialogTitle>
            <DialogContent>
                <Typography sx={{fontSize: 13, color: Grey7}}>The project's History keeps a note that it was removed.</Typography>
                {refusal && <Typography sx={{fontSize: 12.5, color: "#B3261E", mt: 1.5}}>{refusal}</Typography>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} sx={{textTransform: "none"}}>Cancel</Button>
                <Button variant="contained" color="error" disabled={busy} sx={{textTransform: "none"}} onClick={() => save({})}>Remove</Button>
            </DialogActions>
        </Dialog>
    );
}
